import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export const generateBillPDF = (billData) => {
  try {
    // Initialize PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Company Header
    doc.setFillColor(188, 150, 94); // #BC965E
    doc.rect(0, 0, pageWidth, 35, "F");
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("GINNI FASHION STUDIO", pageWidth / 2, 15, { align: "center" });
    
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text("INVOICE", pageWidth / 2, 27, { align: "center" });
    
    // Reset text color for rest of document
    doc.setTextColor(0, 0, 0);
    
    // Bill Information Section
    let yPos = 45;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Bill No: ${billData.BillNumber}`, 14, yPos);
    
    const formattedDate = new Date(billData.CreatedAt).toLocaleString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    doc.text(`Date: ${formattedDate}`, pageWidth - 14, yPos, { align: "right" });
    
    yPos += 7;
    doc.text(`Token No: ${billData.CustomerId || "N/A"}`, 14, yPos);
    doc.text(`Phone: ${billData.PhoneNumber || "N/A"}`, pageWidth - 14, yPos, { align: "right" });
    
    yPos += 7;
    doc.text(`Customer: ${billData.CustomerName}`, 14, yPos);
    doc.text(`Billed By: ${billData.BilledByName}`, pageWidth - 14, yPos, { align: "right" });
    
    // Due Date (if available) - move to next line to avoid overlap
    if (billData.DueDate) {
      yPos += 7;
      const dueDate = new Date(billData.DueDate).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      doc.text(`Due Date: ${dueDate}`, pageWidth - 14, yPos, { align: "right" });
    }
    
    // Order Description (if available)
    if (billData.OrderDescription && billData.OrderDescription.trim() !== '') {
      yPos += 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Order Description:", 14, yPos);
      yPos += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const descLines = doc.splitTextToSize(billData.OrderDescription, pageWidth - 28);
      doc.text(descLines, 14, yPos);
      yPos += (descLines.length * 4) + 5;
    }
    
    yPos += 10;
    
    // Items Table
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("ITEMS", 14, yPos);
    yPos += 5;
    
    // Prepare table data
    const tableData = [];
    
    if (billData.items && billData.items.length > 0) {
      billData.items.forEach((item) => {
        // Main item row
        tableData.push([
          item.ProductCode,
          item.Size || "N/A",
          item.Quantity.toString(),
          `Rs ${parseFloat(item.UnitPrice).toFixed(2)}`,
          item.Discount && parseFloat(item.Discount) > 0 ? `Rs ${parseFloat(item.Discount).toFixed(2)}` : '-',
          `Rs ${parseFloat(item.LineTotal).toFixed(2)}`,
        ]);
        
        // Customizations sub-rows (filter out default stitching entries)
        if (item.customizations && item.customizations.length > 0) {
          item.customizations
            .filter(custom => 
              // Filter out default stitching entries (zero cost and generic description)
              !(parseFloat(custom.Cost) === 0 && custom.Description.startsWith('Stitching for'))
            )
            .forEach((custom) => {
              tableData.push([
                {
                  content: `  ${custom.Type}: ${custom.Description} - Rs ${parseFloat(custom.Cost).toFixed(2)}`,
                  colSpan: 6,
                  styles: { fontStyle: "italic", textColor: [100, 100, 100] },
                },
              ]);
            });
        }
      });
    }
    
    autoTable(doc, {
      startY: yPos,
      head: [["Code", "Size", "Qty", "Price", "D", "Total"]],
      body: tableData,
      theme: "striped",
      headStyles: {
        fillColor: [188, 150, 94],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 20 },
        2: { cellWidth: 15, halign: "right" },
        3: { cellWidth: 30, halign: "right" },
        4: { cellWidth: 25, halign: "right" },
        5: { cellWidth: 30, halign: "right" },
      },
    });
    
    // Bill Summary Section
    yPos = doc.lastAutoTable.finalY + 15;
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("SUMMARY", 14, yPos);
    yPos += 7;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    const summaryX = pageWidth - 70;
    const labelX = summaryX - 10;
    
    doc.text("Subtotal:", labelX, yPos, { align: "right" });
    doc.text(`Rs ${parseFloat(billData.SubTotal).toFixed(2)}`, summaryX, yPos);
    yPos += 6;
    
    doc.text("D:", labelX, yPos, { align: "right" });
    doc.text(`- Rs ${parseFloat(billData.Discount || 0).toFixed(2)}`, summaryX, yPos);
    yPos += 4;
    
    // Draw line exactly between Discount and Total Amount
    doc.setLineWidth(0.5);
    doc.line(labelX - 40, yPos, summaryX + 30, yPos);
    yPos += 4;
    
    doc.setFont("helvetica", "bold");
    doc.text("Total Amount:", labelX, yPos, { align: "right" });
    doc.text(`Rs ${parseFloat(billData.TotalAmount).toFixed(2)}`, summaryX, yPos);
    yPos += 6;
    
    doc.setFont("helvetica", "normal");
    doc.text("Advance Paid:", labelX, yPos, { align: "right" });
    doc.text(`- Rs ${parseFloat(billData.Advance || 0).toFixed(2)}`, summaryX, yPos);
    yPos += 6;
    
    // Scheme Credit (if applied)
    if (billData.SchemeCreditApplied && parseFloat(billData.SchemeCreditApplied) > 0) {
      doc.setTextColor(156, 39, 176); // Purple color for scheme credit
      doc.text("Scheme Credit:", labelX, yPos, { align: "right" });
      doc.text(`- Rs ${parseFloat(billData.SchemeCreditApplied).toFixed(2)}`, summaryX, yPos);
      doc.setTextColor(0, 0, 0); // Reset to black
      yPos += 6;
    }
    
    // Coupon Discount (if applied)
    if (billData.CouponDiscount && parseFloat(billData.CouponDiscount) > 0) {
      doc.setTextColor(33, 150, 243); // Blue color for coupon discount
      doc.text("Coupon Discount:", labelX, yPos, { align: "right" });
      doc.text(`- Rs ${parseFloat(billData.CouponDiscount).toFixed(2)}`, summaryX, yPos);
      doc.setTextColor(0, 0, 0); // Reset to black
      yPos += 6;
    }
    
    // Draw line exactly between Coupon Discount and Balance Due
    yPos += 2;
    doc.setLineWidth(0.5);
    doc.line(labelX - 40, yPos, summaryX + 30, yPos);
    yPos += 4;
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("BALANCE DUE:", labelX, yPos, { align: "right" });
    doc.text(`Rs ${parseFloat(billData.Balance).toFixed(2)}`, summaryX, yPos);
    
    // Coupon Information Section
    yPos += 15;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("COUPON INFORMATION", 14, yPos);
    yPos += 8;
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    

    
    // New Coupon Generated
    if (billData.CouponCode) {
      // Use the actual coupon amount from the customer record, not calculated from TotalAmount
      const newCouponAmount = billData.CouponAmount ? parseFloat(billData.CouponAmount).toFixed(2) : '0.00';
      doc.text("New Coupon Generated:", 14, yPos);
      doc.setFont("helvetica", "bold");
      doc.text(`${billData.CouponCode} (Rs ${newCouponAmount})`, 80, yPos);
    } else {
      doc.text("New Coupon Generated:", 14, yPos);
      doc.setFont("helvetica", "bold");
      doc.text("N/A", 80, yPos);
    }
    
    // Customization Feedback (if available)
    if (billData.WasCustomizationGood && billData.WasCustomizationGood !== "N/A") {
      yPos += 15;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Customization Feedback: ${billData.WasCustomizationGood}`, 14, yPos);
    }
    
    // Save PDF
    doc.save(`Bill-${billData.BillNumber}.pdf`);
    
  } catch (error) {
    console.error("PDF generation error:", error);
    throw error;
  }
};

export const generateBusinessReportPDF = (data) => {
  try {
    const { customers, staff, suppliers, dateRange } = data;
    
    // Initialize PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Company Header
    doc.setFillColor(46, 125, 50); // Green color
    doc.rect(0, 0, pageWidth, 35, "F");
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("BUSINESS REPORT", pageWidth / 2, 15, { align: "center" });
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    const currentDate = new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    // Show date range in header
    let dateRangeText = 'All Time';
    if (dateRange) {
      if (dateRange.type === 'today') dateRangeText = 'Today';
      else if (dateRange.type === 'thisWeek') dateRangeText = 'This Week';
      else if (dateRange.type === 'thisMonth') dateRangeText = 'This Month';
      else if (dateRange.type === 'thisYear') dateRangeText = 'This Year';
      else if (dateRange.type === 'custom' && dateRange.startDate && dateRange.endDate) {
        dateRangeText = `${dateRange.startDate} to ${dateRange.endDate}`;
      }
    }
    
    doc.text(`Generated: ${currentDate}`, pageWidth / 2, 22, { align: "center" });
    doc.text(`Period: ${dateRangeText}`, pageWidth / 2, 29, { align: "center" });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    let yPos = 50;
    
    // Calculate totals
    const totals = calculateBusinessTotals(customers, staff, suppliers);
    
    // Summary Section
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("BUSINESS SUMMARY", 14, yPos);
    yPos += 15;
    
    // Summary box
    doc.setFillColor(245, 245, 245);
    doc.rect(14, yPos, pageWidth - 28, 40, "F");
    doc.rect(14, yPos, pageWidth - 28, 40, "S");
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("NET BALANCE:", 20, yPos + 12);
    doc.setFontSize(14);
    const netBalance = totals.overall.netBalance;
    const balanceColor = netBalance >= 0 ? [46, 125, 50] : [211, 47, 47];
    doc.setTextColor(...balanceColor);
    doc.text(`Rs ${Math.abs(netBalance).toFixed(2)}`, 20, yPos + 25);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(netBalance >= 0 ? '(Amount Due to Business)' : '(Amount Business Owes)', 20, yPos + 35);
    
    yPos += 50;
    
    // Summary Table
    const summaryData = [
      ['Category', 'Count', 'Total Due', 'Total Advance'],
      ['Customers', totals.customers.count.toString(), `Rs ${totals.customers.totalDue.toFixed(2)}`, `Rs ${totals.customers.totalAdvance.toFixed(2)}`],
      ['Staff', totals.staff.count.toString(), `Rs ${totals.staff.totalDue.toFixed(2)}`, `Rs ${totals.staff.totalAdvance.toFixed(2)}`],
      ['Suppliers', totals.suppliers.count.toString(), `Rs ${totals.suppliers.totalDue.toFixed(2)}`, `Rs ${totals.suppliers.totalAdvance.toFixed(2)}`],
      ['TOTAL', totals.overall.totalEntities.toString(), `Rs ${totals.overall.totalDue.toFixed(2)}`, `Rs ${totals.overall.totalAdvance.toFixed(2)}`]
    ];
    
    autoTable(doc, {
      startY: yPos,
      head: [summaryData[0]],
      body: summaryData.slice(1),
      theme: "striped",
      headStyles: {
        fillColor: [46, 125, 50],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      styles: {
        fontSize: 10,
        cellPadding: 4,
      },
      columnStyles: {
        1: { halign: "center" },
        2: { halign: "right" },
        3: { halign: "right" },
      },
    });
    
    // Customer Section
    if (customers && customers.length > 0) {
      doc.addPage();
      yPos = 20;
      
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("CUSTOMER REPORT", 14, yPos);
      yPos += 10;
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`${totals.customers.count} customers | Rs ${totals.customers.totalDue.toFixed(2)} Due | Rs ${totals.customers.totalAdvance.toFixed(2)} Advance`, 14, yPos);
      yPos += 15;
      
      const customerTableData = customers.map(customer => {
        const balance = parseFloat(customer.CurrentBalance) || 0;
        const advance = balance < 0 ? Math.abs(balance) : 0;
        const due = balance > 0 ? balance : 0;
        
        return [
          customer.CustomerName || 'N/A',
          customer.PhoneNumber || 'N/A',
          advance > 0 ? `Rs ${advance.toFixed(2)}` : '',
          due > 0 ? `Rs ${due.toFixed(2)}` : ''
        ];
      });
      
      autoTable(doc, {
        startY: yPos,
        head: [['Customer Name', 'Phone', 'Advance', 'Due']],
        body: customerTableData,
        theme: "striped",
        headStyles: {
          fillColor: [46, 125, 50],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40 },
          2: { cellWidth: 40, halign: "center" },
          3: { cellWidth: 40, halign: "center" },
        },
      });
    }
    
    // Staff Section
    if (staff && staff.length > 0) {
      doc.addPage();
      yPos = 20;
      
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("STAFF REPORT", 14, yPos);
      yPos += 10;
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`${totals.staff.count} staff members | Rs ${totals.staff.totalDue.toFixed(2)} Due | Rs ${totals.staff.totalAdvance.toFixed(2)} Advance`, 14, yPos);
      yPos += 15;
      
      const staffTableData = staff.map(member => {
        const balance = parseFloat(member.CurrentBalance) || 0;
        const advance = balance < 0 ? Math.abs(balance) : 0;
        const due = balance > 0 ? balance : 0;
        
        return [
          member.StaffName || member.FullName || 'N/A',
          member.PhoneNumber || 'N/A',
          advance > 0 ? `Rs ${advance.toFixed(2)}` : '',
          due > 0 ? `Rs ${due.toFixed(2)}` : ''
        ];
      });
      
      autoTable(doc, {
        startY: yPos,
        head: [['Staff Name', 'Phone', 'Advance', 'Due']],
        body: staffTableData,
        theme: "striped",
        headStyles: {
          fillColor: [46, 125, 50],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40 },
          2: { cellWidth: 40, halign: "center" },
          3: { cellWidth: 40, halign: "center" },
        },
      });
    }
    
    // Supplier Section
    if (suppliers && suppliers.length > 0) {
      doc.addPage();
      yPos = 20;
      
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("SUPPLIER REPORT", 14, yPos);
      yPos += 10;
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`${totals.suppliers.count} suppliers | Rs ${totals.suppliers.totalDue.toFixed(2)} Due | Rs ${totals.suppliers.totalAdvance.toFixed(2)} Advance`, 14, yPos);
      yPos += 15;
      
      const supplierTableData = suppliers.map(supplier => {
        const balance = parseFloat(supplier.CurrentBalance) || 0;
        const advance = balance < 0 ? Math.abs(balance) : 0;
        const due = balance > 0 ? balance : 0;
        
        return [
          supplier.SupplierName || supplier.SupplierCode || 'N/A',
          supplier.PhoneNumber || 'N/A',
          advance > 0 ? `Rs ${advance.toFixed(2)}` : '',
          due > 0 ? `Rs ${due.toFixed(2)}` : ''
        ];
      });
      
      autoTable(doc, {
        startY: yPos,
        head: [['Supplier Name', 'Phone', 'Advance', 'Due']],
        body: supplierTableData,
        theme: "striped",
        headStyles: {
          fillColor: [46, 125, 50],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40 },
          2: { cellWidth: 40, halign: "center" },
          3: { cellWidth: 40, halign: "center" },
        },
      });
    }
    
    // Save PDF
    const dateStr = new Date().toISOString().split('T')[0];
    doc.save(`business-report-${dateStr}.pdf`);
    
  } catch (error) {
    console.error("PDF generation error:", error);
    throw error;
  }
};

// Calculate totals helper function for business report
const calculateBusinessTotals = (customers, staff, suppliers) => {
  // Ensure all inputs are arrays
  const customersArray = Array.isArray(customers) ? customers : [];
  const staffArray = Array.isArray(staff) ? staff : [];
  const suppliersArray = Array.isArray(suppliers) ? suppliers : [];
  
  const customerTotals = customersArray.reduce((acc, customer) => {
    const balance = parseFloat(customer.CurrentBalance) || 0;
    if (balance > 0) {
      acc.totalDue += balance;
    } else {
      acc.totalAdvance += Math.abs(balance);
    }
    acc.count += 1;
    return acc;
  }, { totalDue: 0, totalAdvance: 0, count: 0 });

  const staffTotals = staffArray.reduce((acc, member) => {
    const balance = parseFloat(member.CurrentBalance) || 0;
    if (balance > 0) {
      acc.totalDue += balance;
    } else {
      acc.totalAdvance += Math.abs(balance);
    }
    acc.count += 1;
    return acc;
  }, { totalDue: 0, totalAdvance: 0, count: 0 });

  const supplierTotals = suppliersArray.reduce((acc, supplier) => {
    const balance = parseFloat(supplier.CurrentBalance) || 0;
    if (balance > 0) {
      acc.totalDue += balance;
    } else {
      acc.totalAdvance += Math.abs(balance);
    }
    acc.count += 1;
    return acc;
  }, { totalDue: 0, totalAdvance: 0, count: 0 });

  return {
    customers: customerTotals,
    staff: staffTotals,
    suppliers: supplierTotals,
    overall: {
      totalDue: customerTotals.totalDue + staffTotals.totalDue + supplierTotals.totalDue,
      totalAdvance: customerTotals.totalAdvance + staffTotals.totalAdvance + supplierTotals.totalAdvance,
      netBalance: (customerTotals.totalDue + staffTotals.totalDue + supplierTotals.totalDue) - 
                  (customerTotals.totalAdvance + staffTotals.totalAdvance + supplierTotals.totalAdvance),
      totalEntities: customerTotals.count + staffTotals.count + supplierTotals.count
    }
  };
};